/**
 * Modern Workspace Layout Component
 * 
 * A modern workspace with resizable panels, canvas area, and property panels
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  Switch,
  FormControlLabel,
  Chip,
  Stack,
  Button,
  Tooltip,
  useTheme,
  alpha,
  Collapse,
  Card,
  CardContent,
} from '@mui/material';
import {
  Close as CloseIcon,
  DragIndicator as DragIcon,
  Layers as LayersIcon,
  Settings as SettingsIcon,
  Palette as PaletteIcon,
  Timeline as TimelineIcon,
  Info as InfoIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  CenterFocusStrong as CenterIcon,
  GridOn as GridIcon,
  Straighten as RulerIcon,
  Lock as LockIcon,
  LockOpen as UnlockIcon,
  Visibility as VisibleIcon,
  VisibilityOff as HiddenIcon,
} from '@mui/icons-material';

interface ModernWorkspaceProps {
  children?: React.ReactNode;
}

interface Layer {
  id: string;
  name: string;
  visible: boolean;
  locked: boolean;
  type: 'floor' | 'walls' | 'furniture' | 'devices' | 'coverage';
}

const ModernWorkspace: React.FC<ModernWorkspaceProps> = ({ children }) => {
  const theme = useTheme();
  const [leftPanelOpen, setLeftPanelOpen] = useState(true);
  const [rightPanelOpen, setRightPanelOpen] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [rightTab, setRightTab] = useState(0);
  const [zoom, setZoom] = useState(100);
  const [showGrid, setShowGrid] = useState(true);
  const [showRuler, setShowRuler] = useState(true);
  const [layers, setLayers] = useState<Layer[]>([
    { id: '1', name: 'Floor Plan', visible: true, locked: false, type: 'floor' },
    { id: '2', name: 'Walls & Doors', visible: true, locked: false, type: 'walls' },
    { id: '3', name: 'Furniture', visible: true, locked: false, type: 'furniture' },
    { id: '4', name: 'Audio Devices', visible: true, locked: false, type: 'devices' },
    { id: '5', name: 'Coverage Map', visible: false, locked: false, type: 'coverage' },
  ]);

  const toggleLayer = (id: string, field: 'visible' | 'locked') => {
    setLayers(layers.map(layer => 
      layer.id === id ? { ...layer, [field]: !layer[field] } : layer
    ));
  };

  const panelWidth = 320;

  return (
    <Box
      sx={{
        display: 'flex',
        height: 'calc(100vh - 64px)',
        marginTop: '64px',
        backgroundColor: theme.palette.grey[50],
      }}
    >
      {/* Left Panel - Tools & Layers */}
      <Collapse in={leftPanelOpen} orientation="horizontal">
        <Paper
          elevation={0}
          sx={{
            width: panelWidth,
            height: '100%',
            borderRight: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: theme.palette.background.paper,
          }}
        >
          {/* Panel Header */}
          <Box
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography variant="subtitle1" fontWeight="medium">
              Tools & Layers
            </Typography>
            <IconButton size="small" onClick={() => setLeftPanelOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Tabs */}
          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Layers" icon={<LayersIcon />} iconPosition="start" />
            <Tab label="Tools" icon={<SettingsIcon />} iconPosition="start" />
          </Tabs>

          {/* Tab Content */}
          <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
            {activeTab === 0 && (
              <Stack spacing={1}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                  LAYERS
                </Typography>
                {layers.map((layer) => (
                  <Card
                    key={layer.id}
                    variant="outlined"
                    sx={{
                      backgroundColor: alpha(theme.palette.action.hover, 0.02),
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.action.hover, 0.08),
                      },
                    }}
                  >
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DragIcon fontSize="small" sx={{ color: 'action.disabled' }} />
                        <Typography variant="body2" sx={{ flexGrow: 1 }}>
                          {layer.name}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => toggleLayer(layer.id, 'visible')}
                        >
                          {layer.visible ? <VisibleIcon fontSize="small" /> : <HiddenIcon fontSize="small" />}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => toggleLayer(layer.id, 'locked')}
                        >
                          {layer.locked ? <LockIcon fontSize="small" /> : <UnlockIcon fontSize="small" />}
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
                <Button
                  startIcon={<AddIcon />}
                  variant="outlined"
                  size="small"
                  sx={{ mt: 1 }}
                >
                  Add Layer
                </Button>
              </Stack>
            )}

            {activeTab === 1 && (
              <Stack spacing={3}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    ROOM DIMENSIONS
                  </Typography>
                  <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField
                      label="Width"
                      size="small"
                      defaultValue="20"
                      InputProps={{ endAdornment: 'ft' }}
                    />
                    <TextField
                      label="Length"
                      size="small"
                      defaultValue="14"
                      InputProps={{ endAdornment: 'ft' }}
                    />
                    <TextField
                      label="Height"
                      size="small"
                      defaultValue="9.5"
                      InputProps={{ endAdornment: 'ft' }}
                    />
                  </Stack>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    GRID SETTINGS
                  </Typography>
                  <Stack spacing={2} sx={{ mt: 1 }}>
                    <FormControlLabel
                      control={<Switch checked={showGrid} onChange={(e) => setShowGrid(e.target.checked)} />}
                      label="Show Grid"
                    />
                    <FormControlLabel
                      control={<Switch checked={showRuler} onChange={(e) => setShowRuler(e.target.checked)} />}
                      label="Show Rulers"
                    />
                    <FormControl size="small">
                      <InputLabel>Grid Size</InputLabel>
                      <Select defaultValue="1" label="Grid Size">
                        <MenuItem value="0.5">0.5 ft</MenuItem>
                        <MenuItem value="1">1 ft</MenuItem>
                        <MenuItem value="2">2 ft</MenuItem>
                        <MenuItem value="5">5 ft</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                </Box>
              </Stack>
            )}
          </Box>
        </Paper>
      </Collapse>

      {/* Main Canvas Area */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Canvas Toolbar */}
        <Paper
          elevation={0}
          sx={{
            p: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          {!leftPanelOpen && (
            <Tooltip title="Show Left Panel">
              <IconButton size="small" onClick={() => setLeftPanelOpen(true)}>
                <LayersIcon />
              </IconButton>
            </Tooltip>
          )}

          <Divider orientation="vertical" flexItem />

          {/* Zoom Controls */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton size="small" onClick={() => setZoom(Math.max(25, zoom - 25))}>
              <ZoomOutIcon fontSize="small" />
            </IconButton>
            <Chip label={`${zoom}%`} size="small" />
            <IconButton size="small" onClick={() => setZoom(Math.min(200, zoom + 25))}>
              <ZoomInIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => setZoom(100)}>
              <CenterIcon fontSize="small" />
            </IconButton>
          </Box>

          <Divider orientation="vertical" flexItem />

          {/* View Options */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Toggle Grid">
              <IconButton
                size="small"
                color={showGrid ? 'primary' : 'default'}
                onClick={() => setShowGrid(!showGrid)}
              >
                <GridIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Toggle Rulers">
              <IconButton
                size="small"
                color={showRuler ? 'primary' : 'default'}
                onClick={() => setShowRuler(!showRuler)}
              >
                <RulerIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {/* Status */}
          <Stack direction="row" spacing={1}>
            <Chip label="X: 125.5" size="small" variant="outlined" />
            <Chip label="Y: 87.2" size="small" variant="outlined" />
            <Chip label="Snap: ON" size="small" color="primary" variant="outlined" />
          </Stack>

          {!rightPanelOpen && (
            <Tooltip title="Show Properties Panel">
              <IconButton size="small" onClick={() => setRightPanelOpen(true)}>
                <InfoIcon />
              </IconButton>
            </Tooltip>
          )}
        </Paper>

        {/* Canvas */}
        <Box
          sx={{
            flexGrow: 1,
            backgroundColor: theme.palette.grey[100],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {children || (
            <Paper
              elevation={2}
              sx={{
                width: '80%',
                height: '80%',
                backgroundColor: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transform: `scale(${zoom / 100})`,
                transition: 'transform 0.2s',
              }}
            >
              <Typography variant="h6" color="text.secondary">
                Canvas Area
              </Typography>
            </Paper>
          )}
        </Box>
      </Box>

      {/* Right Panel - Properties */}
      <Collapse in={rightPanelOpen} orientation="horizontal">
        <Paper
          elevation={0}
          sx={{
            width: panelWidth,
            height: '100%',
            borderLeft: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: theme.palette.background.paper,
          }}
        >
          {/* Panel Header */}
          <Box
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography variant="subtitle1" fontWeight="medium">
              Properties
            </Typography>
            <IconButton size="small" onClick={() => setRightPanelOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Tabs */}
          <Tabs
            value={rightTab}
            onChange={(_, value) => setRightTab(value)}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Properties" />
            <Tab label="Style" />
            <Tab label="Info" />
          </Tabs>

          {/* Tab Content */}
          <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
            {rightTab === 0 && (
              <Stack spacing={3}>
                <Typography variant="body2" color="text.secondary">
                  Select an object to view its properties
                </Typography>
              </Stack>
            )}

            {rightTab === 1 && (
              <Stack spacing={3}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    APPEARANCE
                  </Typography>
                  <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField
                      label="Fill Color"
                      size="small"
                      defaultValue="#1863D3"
                      type="color"
                    />
                    <TextField
                      label="Stroke Color"
                      size="small"
                      defaultValue="#000000"
                      type="color"
                    />
                    <Box>
                      <Typography variant="body2" gutterBottom>
                        Opacity
                      </Typography>
                      <Slider
                        defaultValue={100}
                        valueLabelDisplay="auto"
                        step={10}
                        marks
                        min={0}
                        max={100}
                      />
                    </Box>
                  </Stack>
                </Box>
              </Stack>
            )}

            {rightTab === 2 && (
              <Stack spacing={2}>
                <Typography variant="caption" color="text.secondary">
                  PROJECT INFORMATION
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="Project Name" secondary="Conference Room A" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Created" secondary="Nov 15, 2024" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Modified" secondary="Today, 3:45 PM" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Objects" secondary="24 items" />
                  </ListItem>
                </List>
              </Stack>
            )}
          </Box>
        </Paper>
      </Collapse>
    </Box>
  );
};

export default ModernWorkspace;